import { Country } from './../../models/common-data/country';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { EventValidationService } from 'app/event/service/event-validation.service';
import { Observable } from 'rxjs/Observable';
import { MenuStore } from '../../../menus/service/menu.store';
import { AuthenticationStore } from '../../../permission/services/authentication.store';
import { FlashMessagesService } from '../../../shared/flash-messages/service/flash.messages.service';
import { DateUtil } from '../../../shared/utils/date.util';
import { ResponseUtil } from '../../../shared/utils/response-util';
import { ConfigLookupType } from '../../models/config/config-lookup-type';
import { ConfigResponse } from '../../models/config/config-response';
import { OnyxEvent } from '../../models/event';
import { Payment } from '../../models/payment';
import { EventStore } from '../../service/event.store';
import { GroupPayConfigStore } from '../../service/grouppay-config.store';
import { OoivRulesStore } from '../../service/ooiv-rules.store';
import { PaymentStore } from '../../service/payment.store';
import { ReportService } from '../../service/report.service';
import { EventUtils } from '../../utils/event-utils';
import { PaymentStatus } from './../../models/payment-status.enum';
import { ReportType } from './../../models/report-type.enum';
import { Unsubscriber } from './../../../shared/helper/unsubscriber';
import { FlashMessage } from 'app/shared/flash-messages/flash.message.record';
import { Router } from '@angular/router';

@Component({
  selector: 'pioneer-commission',
  templateUrl: './commission.component.html',
  styleUrls: ['./commission.component.css']
})
export class CommissionComponent extends Unsubscriber implements OnInit {

  commissionForm: FormGroup;
  onyxEvent: OnyxEvent;
  latestPayment: Payment;
  finalPaymentStatusList: Payment[];
  prepayment: Payment;
  displayedPrepayment = false;
  isCollapsedAddAdjustment = true;
  private hasPrepayment: boolean;
  private isFinalPaymentAuthorized: boolean;
  canMakePrepayment = false;
  showPrepaymentHintText = true;

  ui = {
    showAddPayment: false,
    showPaymentRecord: false,
    showPaymentSummary: false,
    showDownloadDropdown: false
  };

  minAmountPrepayment: number;
  ooivRulesValidation = true;
  ooivEntityReady = true;
  canAddAdjustment$: Observable<boolean>;

  constructor(
    private formBuilder: FormBuilder,
    private menuStore: MenuStore,
    private eventStore: EventStore,
    private paymentStore: PaymentStore,
    private authenticationStore: AuthenticationStore,
    private groupPayConfigStore: GroupPayConfigStore,
    private reportService: ReportService,
    private eventValidationService: EventValidationService,
    private flashMessagesService: FlashMessagesService,
    private translateService: TranslateService,
    private ooivRulesStore: OoivRulesStore,
    private router: Router
  ) {
    super();
  }

  ngOnInit() {

    this.addSubscription(this.eventStore.selectedEvent.subscribe((event: OnyxEvent) => {
      this.onyxEvent = new OnyxEvent(event);
    }));

    this.addSubscription(this.paymentStore.getVendorFinalPaymentStatusList(
      this.onyxEvent.eventProperty.eventPartyId).subscribe((finalPaymentStatusList: Payment[]) => {
      this.finalPaymentStatusList = finalPaymentStatusList;
    }));

    this.addSubscription(this.paymentStore.getLatestVendorFinalPayments().subscribe((latestPayments: Payment[]) => {
      this.latestPayment = latestPayments != null && latestPayments.length > 0 ? latestPayments[0] : undefined;
      const latestFinalPaymentStatus = this.latestPayment ? this.latestPayment.status : undefined;
      if (latestFinalPaymentStatus) {
        this.ui.showPaymentSummary =  [PaymentStatus.REQUEST_REJECTED, PaymentStatus.REJECTED_WF_APPROVAL]
          .every(status => status !== latestFinalPaymentStatus);
        this.ui.showAddPayment = !this.ui.showPaymentSummary;
        this.ui.showDownloadDropdown = true;
        this.isFinalPaymentAuthorized = latestFinalPaymentStatus !== PaymentStatus.REJECTED_WF_APPROVAL;
      } else {
        this.ui.showPaymentSummary = false;
        this.ui.showAddPayment = true;
        this.ui.showDownloadDropdown = false;
        this.isFinalPaymentAuthorized = false;
      }
      this.addSubscription(this.getPrepayment().subscribe(prepayment => {
        this.prepayment = prepayment;
        this.hasPrepayment = prepayment ? true : false;
        if (latestFinalPaymentStatus || this.hasPrepayment) {
          this.ui.showPaymentRecord = true;
        } else {
          this.ui.showPaymentRecord = false;
        }
      }));
      this.addSubscription(this.flashMessagesService.closeMessageStream.subscribe((flashMessage: FlashMessage) => {
        if (flashMessage && flashMessage.message
            && flashMessage.message.includes('Unable to determine Tax. Please try again in few minutes.')) {
          this.router.navigate(['/event', this.onyxEvent.eventId, this.onyxEvent.eventProperty.eventPartyId, 'summary']);
        }
      }));
    }));

    this.commissionForm = this.formBuilder.group({});
    if (this.onyxEvent && EventUtils.getEventName(this.onyxEvent, this.authenticationStore.isAgencyUser())) {
      this.menuStore.setPageHeader(EventUtils.getEventName(this.onyxEvent, this.authenticationStore.isAgencyUser()),
        this.onyxEvent.eventProperty.subscriber);
    } else {
      this.menuStore.setPageHeader('EVENT.EVENT_INFO_TITLE', false);
    }

    this.addSubscription(this.getMinAmount().subscribe(minAmount => {
      this.minAmountPrepayment = minAmount;
    }));

    this.checkPrepaymentPermissions();

    this.canAddAdjustment$ = this.eventValidationService.canAddAdjustments();

    this.addSubscription(this.ooivRulesStore.getVendorOoivExceptionRules(this.onyxEvent.eventProperty.eventPartyId)
      .subscribe((rule) => {
        if (rule) {
          this.ooivRulesValidation = rule.ooivExceptionsIsValid;
        }

        if (this.ooivRulesValidation) {
          this.addSubscription(this.ooivRulesStore.getAgencyOoivExceptionRule().subscribe(agencyRule => {
            if (agencyRule) {
              this.ooivEntityReady = agencyRule.ooivExceptionsIsValid;
            }
          }));
        }
      }));

    if (!this.ooivRulesValidation) {
      // this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.ADDITIONAL_TAX_INFO_PROMPT',
      //   {'role': this.authenticationStore.isPropertyUser() ? 'Agency' : 'Property'}));
      this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.ADDITIONAL_TAX_INFO_PROMPT',
        {'role': 'Agency'}));
    }

    if (!this.ooivEntityReady) {
      this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.ENROLLMENT_NOT_COMPLETED_PROMPT'));
    }
  }

  displayPrepaymentClick(): void {
    this.displayedPrepayment = true;
  }

  onAddPrepayment(): void {
    this.displayedPrepayment = false;
    this.hasPrepayment = true;
  }

  onCancelPrepayment(): void {
    this.displayedPrepayment = false;
  }

  isUserApprover(): boolean {
    return this.authenticationStore.hasPermission('AuthorizePayment');
  }

  displayAddPrepaymentButton(): boolean {
    return !this.displayedPrepayment && !this.hasPrepayment && !this.isEventStarted() && !this.isFinalPaymentAuthorized
      && !this.authenticationStore.isAgencyUser();
  }

  private isEventStarted(): boolean {
    const _startDate = EventUtils.getEventStartDate(this.onyxEvent, this.authenticationStore.isAgencyUser());
    const eventStartDate = DateUtil.parseDate(_startDate).getTime();
    return (eventStartDate < Date.now());
  }

  private getPrepayment(): Observable<Payment> {
    return this.paymentStore.getVendorPrepayment(this.onyxEvent.eventProperty.eventPartyId).map((prepayment) => {
      return prepayment && prepayment.status ? prepayment : null;
    }).share();
  }

  private getMinAmount(): Observable<number> {
    return this.groupPayConfigStore.loadConfig(ConfigLookupType.PREPAYMENT_MIN_AMOUNT, this.onyxEvent.eventProperty.chain,
      this.onyxEvent.eventProperty.brand, this.onyxEvent.eventProperty.propertyId, this.onyxEvent.eventAgency.code)
        .map((response: ConfigResponse) => {
          return response ? JSON.parse(response.value) : null;
        });
  }

  private checkPrepaymentPermissions(): void {
    this.showPrepaymentHintText = this.displayAddPrepaymentButton() && !EventUtils.doesEventHaveAgency(this.onyxEvent);
    this.canMakePrepayment = this.isUserApprover() && EventUtils.doesEventHaveAgency(this.onyxEvent);
  }

  addAdjustmentClick(): void {
    this.isCollapsedAddAdjustment = false;
  }

  displayAddAdjustmentButton(): boolean {
    return this.ui.showAddPayment && this.isCollapsedAddAdjustment;
  }

  /**
   *  If it is an agency user and the event property is subscriber,
   *  the addAdjustment button must be disabled
   * @returns {boolean}
   */
  disableAddAdjustmentButton(): boolean {
    let disable: boolean;
    if (this.authenticationStore.isAgencyUser() && this.onyxEvent.eventProperty.subscriber) {
      disable = true;
    } else {
      disable = false;
    }
    return disable;
  }

  onAddAdjustment(): void {
    this.isCollapsedAddAdjustment = true;
  }

  onCancelAdjustment(): void {
    this.isCollapsedAddAdjustment = true;
  }

  getPaymentsReport(fileType: string) {
    this.addSubscription(this.reportService.getPaymentsReport(this.onyxEvent.eventId, this.onyxEvent.eventProperty.eventPartyId, fileType)
      .subscribe(blob => {
        let filename = ResponseUtil.getFileNameFromHttpResponse(blob);
        if (!filename) {
          filename = this.reportService.buildReportFileName(ReportType.PAYMENT_SUMMARY, this.onyxEvent, fileType);
        }
        saveAs(blob.body, filename);
    }));
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/payment/commission/commission.component.ts